import { AxiosError } from 'axios';
import axiosClient from 'services/network/apiClient';
import { createPayment } from 'services/network/api/payment';
import { addToast } from '../toasts/actions';
import { getValidInvestmentAmount } from '../account/actions';
import { AppDispatch, RootState } from 'store/types';
import { logError } from 'utils/logs';

const setSharesValue = (investment = 500, sharesPrice: number) => {
    if (investment === 0) return sharesPrice;
    const sharesNumber = Math.floor(investment / sharesPrice);
    if (sharesNumber !== Number.parseFloat((investment / sharesPrice).toFixed(5))) {
        return (sharesNumber + 1) * sharesPrice;
    }
    return investment;
};

const getMinPeaAmount = async () => {
    try {
        const { data } = await axiosClient.get('/project/values/minAmountPea');
        return data.value;
    } catch (err) {
        logError(err as Error);
        console.error(err);
        return undefined;
    }
};

export function getInvestmentsData(id: number) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch({ type: 'UPDATE_PROJECT_ID', payload: id });
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('company') === 'true') {
            dispatch({ type: 'SET_ACCOUNT_DATA', payload: { wantInvestBySociety: true } });
        }
        try {
            const { data } = await axiosClient.get(`/project/public/project/${id}`);
            window.sessionStorage.setItem('domain', data.domain);
            const minAmount = setSharesValue(
                data.fund_raised.investment_min_amount,
                data.holding?.valo_unit || Number.parseFloat(data.fund_raised.shares_price)
            );
            const amount = await getMinPeaAmount();
            const minPeaAmount = setSharesValue(
                amount || 10000,
                data.holding?.valo_unit || Number.parseFloat(data.fund_raised.shares_price)
            );
            const amountSought = Number.parseInt(data?.fund_raised?.amount_sought, 10);
            const stockPrice = data.holding?.valo_unit || Number.parseFloat(data.fund_raised.shares_price);

            dispatch({
                type: 'FETCH_INVESTMENTS_DATA',
                payload: {
                    stockPrice,
                    valuation: (data.fund_raised.post_money_valuation || data.fund_raised.pre_money_valuation) * 1000,
                    taxRate: data?.finance?.defisc,
                    amountSought: getValidInvestmentAmount(amountSought, { stockPrice }),
                    name: data.name,
                    legal_name: data.entity.legal_name,
                    domain: data.domain,
                    projectPage: process.env.REACT_APP_DEBUG ? `https://dev-projects.ayomi.fr/${id}` : `https://projects.ayomi.fr/${id}`,
                    id: id,
                    escrowAccount: data.fund_raised.escrow_acct,
                    directlyInSociety: !data?.fund_raised?.holding_is_active,
                    inCharge: data?.in_charge?.ri?.number,
                    inChargeCalendar: data?.in_charge?.ri?.calendly,
                    minAmount,
                    minPeaAmount,
                }
            });
            const amountCached = parseFloat(window.sessionStorage.getItem('amount') || '0');
            if ((urlParams.get('montant') && ((parseFloat(urlParams.get('montant') || '0') >= amountSought)))
                || (urlParams.get('amount') && (parseFloat(urlParams.get('amount') || '0') >= amountSought))) {
                dispatch({ type: 'UPDATE_AMOUNT', payload: amountSought });
                dispatch({ type: 'SET_ACCOUNT_DATA', payload: { investment: getValidInvestmentAmount(amountSought, getState().investments) } });
                window.sessionStorage.setItem('amount', amountSought.toString());
            } else if ((urlParams.get('montant') && ((parseFloat(urlParams.get('montant') || '0') >= minAmount)))
                || (urlParams.get('amount') && (parseFloat(urlParams.get('amount') || '0') >= minAmount))
            ) {
                dispatch({ type: 'UPDATE_AMOUNT', payload: parseFloat(urlParams.get('montant') || urlParams.get('amount') || '0') });
                dispatch({ type: 'SET_ACCOUNT_DATA', payload: { investment: getValidInvestmentAmount(parseFloat(urlParams.get('montant') || urlParams.get('amount') || '0'), getState().investments) } });
                window.sessionStorage.setItem('amount', urlParams.get('montant') || urlParams.get('amount') || '0');
            } else if (amountCached && (amountCached >= minAmount)) {
                dispatch({ type: 'UPDATE_AMOUNT', payload: amountCached });
                dispatch({ type: 'SET_ACCOUNT_DATA', payload: { investment: getValidInvestmentAmount(amountCached, getState().investments) } });
            } else {
                dispatch({ type: 'UPDATE_AMOUNT', payload: minAmount });
                window.sessionStorage.setItem('amount', minAmount.toString());
                dispatch({ type: 'SET_ACCOUNT_DATA', payload: { investment: minAmount } });
            }
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                window.location.assign('/');
            }
            logError(err as Error);
            console.error(err);
        }
    }
}

export function getPaymentRedirectionLink(paymentMethod: string) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch({ type: 'MAKE_PAYMENT_REQUEST' });
        try {
            const investment = getState().investments;
            const { account } = getState();
            if (!window.sessionStorage.getItem('pea')) {
                window.sessionStorage.setItem('pea', 'false');
            }
            if (!window.sessionStorage.getItem('from_company')) {
                window.sessionStorage.setItem('from_company', 'false');
            }
            const value = {
                amount: account.investment,
                type: 'ai',
                success_url: `${window.location.origin}/${investment.id}/confirmation?type=${paymentMethod}`,
                cancel_url: `${window.location.origin}/${investment.id}/paiement?status=cancel`,
                id_project: investment.id,
                payment_method: [paymentMethod],
                escrow_acct: investment.escrowAccount,
                pea: window.sessionStorage.getItem('pea') === 'true',
                from_company: window.sessionStorage.getItem('from_company') === 'true',
                directly_in_society: investment.directlyInSociety,
            };
            const { data } = await createPayment(value);
            window.localStorage.setItem('id_transaction', data.id_transaction);
            if (data.url) {
                window.location.assign(data.url);
            } else {
                dispatch({
                    type: 'MAKE_PAYMENT_SUCCESS', payload: data,
                });
            }
        } catch (err) {
            logError(err as Error);
            dispatch({ type: 'MAKE_PAYMENT_FAILED', payload: err });
            dispatch(addToast({
                type: 'error',
                title: 'Le paiement est momentanément indisponible',
                description: `Une erreur technique est survenue lors de l'envoie de votre demande. ${err}`
            }))
        }
    };
}